import { ChangeEvent } from 'react'
import {
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

import { useTranslation } from 'shared/hooks/useSafeTranslation'
import useStyle from 'shared/hooks/useStyle'
import useDelayedFlag from 'App/Root/Location/hook/useDelayedFlag'

import styles from './CenterSearch.style'

const SPINNER_DELAY_MS = 300

type Props = {
    value: string
    onChange: (value: string) => void
    isSearching?: boolean
}

const CenterSearch = ({
    value,
    onChange,
    isSearching = false,
}: Readonly<Props>) => {
    const { t } = useTranslation()
    const classes = useStyle(styles)
    const showSpinner = useDelayedFlag(isSearching, SPINNER_DELAY_MS)

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value)
    }

    const handleClear = () => {
        onChange('')
    }

    return (
        <TextField
            css={classes.root}
            value={value}
            onChange={handleChange}
            placeholder={t('SEARCH_SERVICE_CENTER')}
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{
                htmlInput: {
                    'aria-label': t('SEARCH_SERVICE_CENTER'),
                    'data-testid': 'service-center-search',
                },
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            {showSpinner ? (
                                <CircularProgress
                                    size={24}
                                    aria-hidden="true"
                                    data-testid="service-center-search-loader"
                                />
                            ) : <SearchIcon />}
                        </InputAdornment>
                    ),
                    endAdornment: value ? (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label={t('CANCEL')}
                                data-testid="service-center-search-clear"
                                onClick={handleClear}
                                edge="end"
                            >
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                },
            }}
        />
    )
}

export default CenterSearch
